/**
 * useDialogDismiss — one hook covering every overlay's a11y + mobile
 * dismissal concerns. Drop into any modal / drawer / sheet to get:
 *
 *   1. Esc key → onClose (when the overlay has focus or focus is inside it).
 *   2. History push on open → browser-back / Android hardware-back / iOS
 *      edge-swipe-back closes the overlay instead of leaving the page.
 *   3. Focus trap: Tab and Shift+Tab cycle inside the overlay; on close,
 *      focus returns to the trigger.
 *   4. Scroll-lock on body while the overlay is open.
 *
 * Usage:
 *
 *   function MyModal({ open, onClose }) {
 *     const ref = useRef(null);
 *     useDialogDismiss({ open, onClose, ref });
 *     if (!open) return null;
 *     return (
 *       <div className="fixed inset-0 z-50 bg-black/60" onClick={onClose}>
 *         <div ref={ref} role="dialog" aria-modal="true" aria-label="..."
 *              onClick={(e) => e.stopPropagation()}>
 *           ...
 *         </div>
 *       </div>
 *     );
 *   }
 *
 * Audit pass 2026-06-08 — applied across ~32 hand-rolled overlays so
 * Android hardware-back / Esc / backdrop-click / focus management all
 * work without per-component plumbing.
 */
import { useEffect, useRef } from "react";

const HISTORY_STATE_KEY = "zcd_dialog_open";

export default function useDialogDismiss({
  open,
  onClose,
  ref,
  // Skip individual concerns when a host has its own implementation.
  // Defaults are ON — opt OUT explicitly.
  esc = true,
  historyBack = true,
  focusTrap = true,
  scrollLock = true,
}) {
  // Remember the element that opened the overlay so we can restore
  // focus on close. The trigger is usually the focused element at the
  // moment `open` flips true.
  const previouslyFocusedRef = useRef(null);

  // ── Esc key ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !esc) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // Don't hijack Esc if the user is composing inside a native
      // select / contenteditable that handles Esc itself.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "select") return;
      e.preventDefault();
      onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, esc, onClose]);

  // ── History push so hardware-back closes overlay ────────────────
  useEffect(() => {
    if (!open || !historyBack) return;
    try {
      const tag = HISTORY_STATE_KEY + "_" + Math.random().toString(36).slice(2, 8);
      window.history.pushState({ [HISTORY_STATE_KEY]: tag }, "");
      const onPop = (e) => {
        // popstate fires whenever the user navigates back in history.
        // We don't care what's in e.state — just close the overlay.
        onClose?.();
      };
      window.addEventListener("popstate", onPop);
      return () => {
        window.removeEventListener("popstate", onPop);
        // If we still own the top history entry (overlay closed via
        // X / backdrop / Esc rather than back-button), pop it so the
        // app's history stays clean. Guard against missing state in
        // case some other code pushed in the meantime.
        try {
          if (window.history.state?.[HISTORY_STATE_KEY] === tag) {
            window.history.back();
          }
        } catch { /* ignore */ }
      };
    } catch { /* SSR / disabled history — silently skip */ }
  }, [open, historyBack, onClose]);

  // ── Body scroll-lock ────────────────────────────────────────────
  useEffect(() => {
    if (!open || !scrollLock) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, scrollLock]);

  // ── Focus trap + restore focus on close ─────────────────────────
  useEffect(() => {
    if (!open || !focusTrap) return;
    previouslyFocusedRef.current = document.activeElement;

    const node = ref?.current;
    if (!node) return;

    // Focus the first focusable element inside on next tick so the
    // mount transition doesn't yank focus mid-animation.
    const focusFirst = () => {
      const focusables = getFocusable(node);
      const target = focusables[0] || node;
      if (target.tabIndex < 0 && !target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }
      target.focus({ preventScroll: true });
    };
    const t = setTimeout(focusFirst, 0);

    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const focusables = getFocusable(node);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      // Restore focus to the trigger when the overlay closes — only
      // if it's still in the document (route changes can detach it).
      const prev = previouslyFocusedRef.current;
      if (prev && document.contains(prev) && typeof prev.focus === "function") {
        prev.focus({ preventScroll: true });
      }
    };
  }, [open, focusTrap, ref]);
}

// Collect focusable elements within the dialog. Pure DOM, no library.
function getFocusable(root) {
  if (!root) return [];
  const sel = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type=hidden])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");
  return Array.from(root.querySelectorAll(sel))
    .filter((el) => !el.hasAttribute("aria-hidden") && el.offsetParent !== null);
}
